import { useState } from 'react';
import { Button, Alert, AlertDescription } from '@/components/ui';
import { Copy, Download, Check, LifeBuoy } from 'lucide-react';

interface Props {
  codes: string[];
  onDone: () => void;
}

/**
 * The one and only time these codes are readable — they're stored hashed, so
 * there's no "show them again" path. The confirm button is therefore gated on
 * the user having actually copied or downloaded them; letting someone dismiss
 * this with a stray click is how people end up enrolled and unrecoverable.
 */
export default function RecoveryCodesDisplay({ codes, onDone }: Props) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(codes.join('\n'));
    setCopied(true); setSaved(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const download = () => {
    const blob = new Blob(
      [`SimCheck.ai recovery codes\n\nEach code works once. Keep them somewhere safe and offline.\n\n${codes.join('\n')}\n`],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'simcheck-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
    setSaved(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <LifeBuoy className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" />
        <div>
          <h3 className="font-medium text-foreground">Save your recovery codes</h3>
          <p className="text-sm text-muted-foreground">
            Use one to sign in if you lose access to your other methods. Each code works once.
          </p>
        </div>
      </div>

      <Alert>
        <AlertDescription>
          This is the only time these codes are shown. If you lose them you'll need to generate a new set.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-2 gap-2 p-4 rounded-lg border border-border bg-muted font-mono text-sm">
        {codes.map((c) => <div key={c} className="text-foreground">{c}</div>)}
      </div>

      <div className="flex gap-2">
        <Button variant="secondary" className="flex-1" onClick={copy}>
          {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button variant="secondary" className="flex-1" onClick={download}>
          <Download className="w-4 h-4 mr-2" /> Download
        </Button>
      </div>

      <Button className="w-full" onClick={onDone} disabled={!saved}>
        {saved ? "I've saved them" : 'Copy or download first'}
      </Button>
    </div>
  );
}
